import React, { useEffect, useRef, useState } from 'react';

export default function CursorGlow() {
  const dotRef = useRef(null);
  const glowRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });
  const dotPos = useRef({ x: 0, y: 0 });
  const glowPos = useRef({ x: 0, y: 0 });
  const [visible, setVisible] = useState(false);
  const [clicking, setClicking] = useState(false);
  const [hovering, setHovering] = useState(false);
  const rafRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia('(hover: none)').matches) return;

    const onMove  = (e) => { mouse.current = { x: e.clientX, y: e.clientY }; setVisible(true); };
    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);
    const onDown  = () => setClicking(true);
    const onUp    = () => setClicking(false);
    const onOver  = (e) => setHovering(!!e.target.closest('a,button,input,textarea,select,[role="button"]'));

    document.addEventListener('mousemove',  onMove);
    document.addEventListener('mouseleave', onLeave);
    document.addEventListener('mouseenter', onEnter);
    document.addEventListener('mousedown',  onDown);
    document.addEventListener('mouseup',    onUp);
    document.addEventListener('mouseover',  onOver);

    const lerp = (a, b, t) => a + (b - a) * t;

    const animate = () => {
      dotPos.current.x  = lerp(dotPos.current.x,  mouse.current.x, 0.35);
      dotPos.current.y  = lerp(dotPos.current.y,  mouse.current.y, 0.35);
      glowPos.current.x = lerp(glowPos.current.x, mouse.current.x, 0.1);
      glowPos.current.y = lerp(glowPos.current.y, mouse.current.y, 0.1);

      if (dotRef.current)
        dotRef.current.style.transform  = `translate(${dotPos.current.x}px, ${dotPos.current.y}px)`;
      if (glowRef.current)
        glowRef.current.style.transform = `translate(${glowPos.current.x}px, ${glowPos.current.y}px)`;

      rafRef.current = requestAnimationFrame(animate);
    };
    animate();

    document.documentElement.style.cursor = 'none';
    return () => {
      document.removeEventListener('mousemove',  onMove);
      document.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('mouseenter', onEnter);
      document.removeEventListener('mousedown',  onDown);
      document.removeEventListener('mouseup',    onUp);
      document.removeEventListener('mouseover',  onOver);
      cancelAnimationFrame(rafRef.current);
      document.documentElement.style.cursor = '';
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  return (
    <>
      {/* Large glassy orb — slow follow */}
      <div ref={glowRef} style={{
        position: 'fixed', top: '-120px', left: '-120px',
        width: '240px', height: '240px', borderRadius: '50%',
        pointerEvents: 'none', zIndex: 9998,
        background: 'radial-gradient(circle, rgba(37,138,203,0.13) 0%, rgba(37,138,203,0.04) 45%, transparent 70%)',
        border: '1px solid rgba(37,138,203,0.08)',
        willChange: 'transform',
      }} />

      {/* Ring — tight follow */}
      <div ref={dotRef} style={{
        position: 'fixed',
        top:  clicking ? '-22px' : hovering ? '-28px' : '-20px',
        left: clicking ? '-22px' : hovering ? '-28px' : '-20px',
        width:  clicking ? '44px' : hovering ? '56px' : '40px',
        height: clicking ? '44px' : hovering ? '56px' : '40px',
        borderRadius: '50%', pointerEvents: 'none', zIndex: 9999,
        background: hovering
          ? 'rgba(37,138,203,0.12)'
          : clicking ? 'rgba(37,138,203,0.22)' : 'rgba(37,138,203,0.07)',
        border: `1.5px solid rgba(37,138,203,${hovering ? 0.7 : clicking ? 0.9 : 0.45})`,
        backdropFilter: 'blur(4px)',
        boxShadow: `0 0 ${clicking ? 20 : hovering ? 16 : 10}px rgba(37,138,203,${clicking ? 0.45 : hovering ? 0.3 : 0.18})`,
        transition: 'width 0.25s cubic-bezier(.23,1,.32,1), height 0.25s cubic-bezier(.23,1,.32,1), top 0.25s, left 0.25s, background 0.2s, box-shadow 0.2s, border-color 0.2s',
        willChange: 'transform',
      }}>
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)',
          width: clicking ? '6px' : '5px', height: clicking ? '6px' : '5px',
          borderRadius: '50%',
          background: `rgba(37,138,203,${clicking ? 1 : 0.85})`,
          boxShadow: '0 0 8px rgba(37,138,203,0.8)',
          transition: 'all 0.2s',
        }} />
      </div>

      <style>{`* { cursor: none !important; }`}</style>
    </>
  );
}